import { CheckCircle2, Circle, Compass, Sparkles, Users } from "lucide-react";

/**
 * Horizontal "pizza tracker"-style Faith Journey stepper.
 * Displays the 7 canonical steps + a side missions-trips badge. Completed
 * steps are filled with a date pill; ongoing steps (serve team, groups)
 * show an "Ongoing" chip; the current furthest step gently pulses.
 *
 * Purely presentational — mutations happen in the editor below.
 */
const STEPS = [
  { key: "accepted_jesus",   label: "Accepted Jesus",              accent: "green" },
  { key: "baptism",          label: "Baptism",                     accent: "terra" },
  { key: "growth_track",     label: "Growth Track",                accent: "green" },
  { key: "serve_team",       label: "Joining A Serve Team",        accent: "terra" },
  { key: "neighborhood_group", label: "Joining A Neighborhood Group", accent: "green" },
  { key: "forward",          label: "FORWARD",                     accent: "terra" },
  { key: "leading_group",    label: "Leading A Neighborhood Group", accent: "green" },
];

function stepState(step, j) {
  switch (step.key) {
    case "accepted_jesus": return { done: !!j.accepted_jesus_date, value: j.accepted_jesus_date };
    case "baptism": return { done: !!j.baptism_date, value: j.baptism_date };
    case "growth_track": return { done: !!j.growth_track_date, value: j.growth_track_date };
    case "forward": return { done: !!j.forward_date, value: j.forward_date };
    case "serve_team": {
      const teams = (j.serve_teams || []).filter((t) => !t.archived);
      return { done: teams.length > 0, value: "Ongoing", detail: teams.map((t) => t.team_name).join(" · ") };
    }
    case "neighborhood_group": {
      const gs = (j.neighborhood_groups || []).filter((g) => !g.archived);
      return { done: gs.length > 0, value: "Ongoing", detail: gs.map((g) => g.group_name).join(" · ") };
    }
    case "leading_group": {
      const gs = (j.leading_groups || []).filter((g) => !g.archived);
      return { done: gs.length > 0, value: "Ongoing", detail: gs.map((g) => g.group_name).join(" · ") };
    }
    default: return { done: false, value: null };
  }
}

export default function FaithJourneyStepper({ journey, current = null, compact = false }) {
  const j = journey || {};
  const states = STEPS.map((s) => ({ step: s, ...stepState(s, j) }));
  // First not-done step becomes the "current"
  const currentKey = current || states.find((s) => !s.done)?.step.key || null;
  const trips = (j.missions_trips || []).filter((t) => !t.archived);

  return (
    <div className={compact ? "space-y-3" : "space-y-4"} data-testid="faith-journey-stepper">
      <div className="flex items-start gap-2 sm:gap-3 overflow-x-auto pb-2">
        {states.map((s, idx) => {
          const isCurrent = s.step.key === currentKey && !s.done;
          const isTerra = s.step.accent === "terra";
          const bubble = s.done
            ? (isTerra ? "bg-terracotta-500 text-white" : "bg-forest-900 text-white")
            : isCurrent
              ? (isTerra
                  ? "bg-terracotta-500/25 text-terracotta-700 animate-pulse"
                  : "bg-forest-900/15 text-forest-900 animate-pulse")
              : "bg-cream-200 text-charcoal-muted";
          return (
            <div
              key={s.step.key}
              className="flex flex-col items-center min-w-[110px] sm:min-w-[130px] shrink-0"
              data-testid={`fj-step-${s.step.key}`}
              data-fj-done={s.done ? "true" : "false"}
            >
              <div className={`h-12 w-12 rounded-full flex items-center justify-center shadow-sm ${bubble}`}>
                {s.done
                  ? <CheckCircle2 className="h-5 w-5" strokeWidth={2} />
                  : <Circle className="h-5 w-5" strokeWidth={1.5} />}
              </div>
              <p className="mt-2 text-[10px] uppercase tracking-widest text-charcoal-muted text-center">
                Step {idx + 1}
              </p>
              <p className="mt-1 text-xs font-medium text-forest-900 text-center leading-tight">
                {s.step.label}
              </p>
              {s.done ? (
                <span className={`mt-1.5 text-[10px] px-2 py-0.5 rounded-full ${isTerra ? "bg-terracotta-500/15 text-terracotta-700" : "bg-forest-900/10 text-forest-900"}`}>
                  {s.value}
                </span>
              ) : (
                <span className="mt-1.5 text-[10px] italic text-charcoal-muted">
                  {isCurrent ? "Up Next" : "Not Yet"}
                </span>
              )}
              {s.detail && (
                <p className="mt-1 text-[10px] text-charcoal-muted text-center line-clamp-2">
                  {s.detail}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {trips.length > 0 && (
        <div className="patria-card p-3 flex items-center gap-3 bg-terracotta-500/5" data-testid="fj-missions-trips-badge">
          <div className="h-9 w-9 rounded-full bg-terracotta-500 text-white flex items-center justify-center">
            <Compass className="h-4 w-4" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-widest text-terracotta-600 font-medium">Missions Trips</p>
            <p className="text-sm text-forest-900">
              {trips.length} {trips.length === 1 ? "trip" : "trips"} logged
            </p>
          </div>
          <div className="flex flex-wrap gap-1 justify-end">
            {trips.slice(0, 3).map((t) => (
              <span key={t.id} className="patria-chip-terra text-[10px]">
                {t.destination}
              </span>
            ))}
            {trips.length > 3 && (
              <span className="text-[10px] text-charcoal-muted">+{trips.length - 3}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
